import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Edit, Trash2, Plus, Calendar } from 'lucide-react';
import API_URL from '../config';
import logger from '../utils/logger';


const TwoWeekPlanner = () => {
  const [rolesAndGoals, setRolesAndGoals] = useState([]);
  const [objectivesAndTasks, setObjectivesAndTasks] = useState({});
  const [newObjectives, setNewObjectives] = useState({});
  const [newTasks, setNewTasks] = useState({});
  const [editingObjective, setEditingObjective] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date(Date.now() + 13 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [expandedRoles, setExpandedRoles] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRolesAndGoals();
    fetchObjectivesAndTasks();
  }, [startDate, endDate]);

  const fetchRolesAndGoals = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/roles-and-goals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Initialize all roles as expanded by default
      const roles = response.data;
      const initialExpandedState = {};
      roles.forEach(role => {
        initialExpandedState[role._id] = true;
      });
      
      setRolesAndGoals(roles);
      setExpandedRoles(initialExpandedState);
    } catch (error) {
      logger.error('Error fetching roles and goals:', error);
      setError('Failed to fetch roles and goals');
    } finally {
      setLoading(false);
    }
  };

  const fetchObjectivesAndTasks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/two-week-planner`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { startDate, endDate }
      });
      setObjectivesAndTasks(response.data);
    } catch (error) {
      logger.error('Error fetching objectives and tasks:', error);
      setError('Failed to fetch objectives and tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleAddObjective = async (e, roleGoalId) => {
    e.preventDefault();
    try {
      // Validate inputs
      if (!roleGoalId) {
        setError('Role/Goal ID is missing');
        return;
      }
      
      const objectiveText = newObjectives[roleGoalId] || '';
      if (!objectiveText.trim()) {
        setError('Objective text cannot be empty');
        return;
      }
      
      if (!startDate || !endDate) {
        setError('Start date and end date are required');
        return;
      }
      
      // Validate dates
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        setError('Invalid date format');
        return;
      }
      
      const token = localStorage.getItem('token');
      console.log('Sending objective data:', {
        roleGoalId,
        objective: objectiveText,
        startDate,
        endDate
      });
      
      const response = await axios.post(`${API_URL}/two-week-planner/objective`, 
        { 
          roleGoalId,
          objective: objectiveText,
          startDate,
          endDate
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setObjectivesAndTasks(prev => {
        // Check if prev[roleGoalId] exists
        if (!prev[roleGoalId]) {
          return {
            ...prev,
            [roleGoalId]: {
              objectives: [response.data],
              tasks: []
            }
          };
        }
        
        return {
          ...prev,
          [roleGoalId]: {
            ...prev[roleGoalId],
            objectives: [...(prev[roleGoalId]?.objectives || []), response.data]
          }
        };
      });
      
      setNewObjectives(prev => ({ ...prev, [roleGoalId]: '' }));
      setMessage('Objective added successfully');
    } catch (error) {
      logger.error('Error adding objective:', error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        logger.error('Response data:', error.response.data);
        setError(`Failed to add objective: ${error.response.data.message || error.message}`);
      } else if (error.request) {
        // The request was made but no response was received
        setError('No response received from server. Please check your connection.');
      } else {
        // Something happened in setting up the request that triggered an Error
        setError(`Failed to add objective: ${error.message}`);
      }
    }
  };

  const handleEditObjective = async (roleGoalId, objectiveId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/two-week-planner/objective/${objectiveId}`, 
        { objective: editingObjective.text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setObjectivesAndTasks(prev => {
        if (!prev[roleGoalId] || !prev[roleGoalId].objectives) {
          return {
            ...prev,
            [roleGoalId]: { objectives: [response.data] }
          };
        }
        return {
          ...prev,
          [roleGoalId]: {
            ...prev[roleGoalId],
            objectives: prev[roleGoalId].objectives.map(obj => 
              obj._id === objectiveId ? response.data : obj
            )
          }
        };
      });
      setEditingObjective(null);
      setMessage('Objective updated successfully');
    } catch (error) {
      logger.error('Error updating objective:', error);
      setError('Failed to update objective');
    }
  };

  const handleDeleteObjective = async (roleGoalId, objectiveId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/two-week-planner/objective/${objectiveId}`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setObjectivesAndTasks(prev => {
        if (!prev[roleGoalId] || !prev[roleGoalId].objectives) {
          return prev;
        }
        return {
          ...prev,
          [roleGoalId]: {
            ...prev[roleGoalId],
            objectives: prev[roleGoalId].objectives.filter(obj => obj._id !== objectiveId)
          }
        };
      });
      setMessage('Objective deleted successfully');
    } catch (error) {
      logger.error('Error deleting objective:', error);
      setError('Failed to delete objective');
    }
  };

  const handleAddTask = async (e, roleGoalId) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/two-week-planner/task`, 
        { 
          roleGoalId,
          task: newTasks[roleGoalId] || '',
          startDate,
          endDate,
          completed: false
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setObjectivesAndTasks(prev => ({
        ...prev,
        [roleGoalId]: {
          ...prev[roleGoalId],
          tasks: [...(prev[roleGoalId]?.tasks || []), response.data]
        }
      }));
      setNewTasks(prev => ({ ...prev, [roleGoalId]: '' }));
      setMessage('Task added successfully');
    } catch (error) {
      logger.error('Error adding task:', error);
      setError('Failed to add task');
    }
  };

  const handleEditTask = async (roleGoalId, taskId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/two-week-planner/task/${taskId}`, 
        { task: editingTask.text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setObjectivesAndTasks(prev => {
        if (!prev[roleGoalId]) {
          return {
            ...prev,
            [roleGoalId]: { tasks: [response.data] }
          };
        }
        return {
          ...prev,
          [roleGoalId]: {
            ...prev[roleGoalId],
            tasks: prev[roleGoalId].tasks?.map(task => 
              task._id === taskId ? response.data : task
            ) || [response.data]
          }
        };
      });
      setEditingTask(null);
      setMessage('Task updated successfully');
    } catch (error) {
      logger.error('Error updating task:', error);
      setError('Failed to update task');
    }
  };

  const handleDeleteTask = async (roleGoalId, taskId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/two-week-planner/task/${taskId}`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setObjectivesAndTasks(prev => {
        if (!prev[roleGoalId] || !prev[roleGoalId].tasks) {
          return prev;
        }
        return {
          ...prev,
          [roleGoalId]: {
            ...prev[roleGoalId],
            tasks: prev[roleGoalId].tasks.filter(task => task._id !== taskId)
          }
        };
      });
      setMessage('Task deleted successfully');
    } catch (error) {
      logger.error('Error deleting task:', error);
      setError('Failed to delete task');
    }
  };

  const handleToggleTask = async (roleGoalId, taskId, completed) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/two-week-planner/task/${taskId}`, 
        { completed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setObjectivesAndTasks(prev => {
        if (!prev[roleGoalId] || !prev[roleGoalId].tasks) {
          return {
            ...prev,
            [roleGoalId]: { tasks: [response.data] }
          };
        }
        return {
          ...prev,
          [roleGoalId]: {
            ...prev[roleGoalId],
            tasks: prev[roleGoalId].tasks.map(task => 
              task._id === taskId ? response.data : task
            )
          }
        };
      });
    } catch (error) {
      logger.error('Error updating task:', error);
      setError('Failed to update task');
    }
  };

  const handleCompleteObjective = async (roleGoalId, objectiveId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/two-week-planner/objective/${objectiveId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setObjectivesAndTasks(prev => {
        if (!prev[roleGoalId] || !prev[roleGoalId].objectives) {
          return prev;
        }
        return {
          ...prev,
          [roleGoalId]: {
            ...prev[roleGoalId],
            objectives: prev[roleGoalId].objectives.filter(obj => obj._id !== objectiveId)
          }
        };
      });
      setMessage('Objective completed and removed');
    } catch (error) {
      logger.error('Error completing objective:', error);
      setError('Failed to complete objective');
    }
  };

  const toggleRole = (roleId) => {
    setExpandedRoles(prev => ({ ...prev, [roleId]: !prev[roleId] }));
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Weekly Planner</h1>
      {error && <p className="text-red-500 mb-4">{error}</p>}
      {message && <p className="text-green-500 mb-4">{message}</p>}

      <div className="mb-4 flex flex-wrap items-center">
        <div className="w-full sm:w-auto mb-2 sm:mb-0 sm:mr-4">
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">Start Date:</label>
          <div className="relative">
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline pr-10"
            />
            <Calendar className="absolute right-3 top-2 text-gray-400" size={20} />
          </div>
        </div>
        <div className="w-full sm:w-auto">
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">End Date:</label>
          <div className="relative">
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline pr-10"
            />
            <Calendar className="absolute right-3 top-2 text-gray-400" size={20} />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      ) : rolesAndGoals.length === 0 ? (
        <p className="text-center py-4">No roles and goals found. Please add some in the Roles and Goals section.</p>
      ) : (
        rolesAndGoals.map((roleGoal) => (
          <div key={roleGoal._id} className="mb-4 bg-white shadow rounded-lg overflow-hidden">
            <div 
              className="flex justify-between items-center p-4 bg-gray-100 cursor-pointer"
              onClick={() => toggleRole(roleGoal._id)}
            >
              <h2 className="text-lg font-semibold">{roleGoal.role}: {roleGoal.goal}</h2>
              {expandedRoles[roleGoal._id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
            
            {expandedRoles[roleGoal._id] && (
              <div className="p-4">
                <h3 className="text-md font-semibold mb-2">Objectives</h3>
                {(objectivesAndTasks[roleGoal._id]?.objectives || []).length === 0 ? (
                  <p className="text-gray-500 italic mb-2">No objectives yet. Add one below.</p>
                ) : (
                  (objectivesAndTasks[roleGoal._id]?.objectives || []).map((objective) => (
                    <div key={objective._id} className="mb-2 flex items-center justify-between">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          onChange={() => handleCompleteObjective(roleGoal._id, objective._id)}
                          className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span>{objective.objective}</span>
                      </div>
                      <div>
                        <button 
                          onClick={() => setEditingObjective({ id: objective._id, text: objective.objective })} 
                          className="text-blue-500 mr-2"
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          onClick={() => handleDeleteObjective(roleGoal._id, objective._id)} 
                          className="text-red-500"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
                {editingObjective && editingObjective.id && (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    handleEditObjective(roleGoal._id, editingObjective.id);
                  }} className="mt-2 mb-4">
                    <div className="flex">
                      <input
                        type="text"
                        value={editingObjective.text}
                        onChange={(e) => setEditingObjective({...editingObjective, text: e.target.value})}
                        className="flex-grow shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mr-2"
                      />
                      <button type="submit" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2">
                        Save
                      </button>
                      <button type="button" onClick={() => setEditingObjective(null)} className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded">
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
                <form onSubmit={(e) => handleAddObjective(e, roleGoal._id)} className="mt-2 mb-4">
                  <div className="flex">
                    <input
                      type="text"
                      value={newObjectives[roleGoal._id] || ''}
                      onChange={(e) => setNewObjectives({...newObjectives, [roleGoal._id]: e.target.value})}
                      placeholder="New objective"
                      className="flex-grow shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mr-2"
                    />
                    <button type="submit" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                      <Plus size={20} />
                    </button>
                  </div>
                </form>

                <h3 className="text-md font-semibold mb-2">Tasks</h3>
                {(objectivesAndTasks[roleGoal._id]?.tasks || []).map((task) => (
                  <div key={task._id} className="mb-2 flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={task.completed}
                        onChange={() => handleToggleTask(roleGoal._id, task._id, !task.completed)}
                        className="mr-2"
                      />
                      <span className={task.completed ? 'line-through' : ''}>{task.task}</span>
                    </div>
                    <div>
                      <button onClick={() => setEditingTask({ id: task._id, text: task.task })} className="text-blue-500 mr-2">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => handleDeleteTask(roleGoal._id, task._id)} className="text-red-500">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                {editingTask && editingTask.id && (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    handleEditTask(roleGoal._id, editingTask.id);
                  }} className="mt-2 mb-4">
                    <div className="flex">
                      <input
                        type="text"
                        value={editingTask.text}
                        onChange={(e) => setEditingTask({...editingTask, text: e.target.value})}
                        className="flex-grow shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mr-2"
                      />
                      <button type="submit" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2">
                        Save
                      </button>
                      <button type="button" onClick={() => setEditingTask(null)} className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded">
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
                <form onSubmit={(e) => handleAddTask(e, roleGoal._id)} className="mt-2">
                  <div className="flex">
                    <input
                      type="text"
                      value={newTasks[roleGoal._id] || ''}
                      onChange={(e) => setNewTasks({...newTasks, [roleGoal._id]: e.target.value})}
                      placeholder="New task"
                      className="flex-grow shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mr-2"
                    />
                    <button type="submit" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                      <Plus size={20} />
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default TwoWeekPlanner;